import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import UserDashboard from 'layouts/UserDashboard';
import Container from 'components/Container';
import { listBusinesses, createBusiness } from './BusinessService';
import { UserBusinessResponse } from '../Financial/types';

const TEMPLATE_TYPES = [
  { value: 'START_UP', label: 'Start-Up' },
  { value: 'SMALL_BUSINESS', label: 'Small Business' },
  { value: 'PRIVATE_EQUITY', label: 'Private Equity' },
];

const templateTypeColor = (
  type: string | null,
): 'primary' | 'success' | 'warning' | 'default' => {
  if (type === 'START_UP') return 'primary';
  if (type === 'SMALL_BUSINESS') return 'success';
  if (type === 'PRIVATE_EQUITY') return 'warning';
  return 'default';
};

const statusColor = (
  status: string,
): 'default' | 'success' | 'error' | 'warning' => {
  if (status === 'ACTIVE') return 'success';
  if (status === 'ARCHIVED') return 'error';
  return 'default';
};

const BusinessHomePage = (): JSX.Element => {
  const navigate = useNavigate();

  const [businesses, setBusinesses] = useState<UserBusinessResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState('START_UP');

  useEffect(() => {
    load();
  }, []);

  function load() {
    setLoading(true);
    setError(null);
    listBusinesses()
      .then((res) => setBusinesses(res.data))
      .catch(() => setError('Failed to load businesses.'))
      .finally(() => setLoading(false));
  }

  function handleCreate() {
    if (!newName.trim()) return;
    setCreating(true);
    createBusiness({ businessName: newName.trim(), templateType: newType })
      .then((res) => {
        navigate(`/user/business/${res.data.id}/setup`);
      })
      .catch(() => {
        setError('Failed to create business.');
        setCreating(false);
      });
  }

  const draftBusinesses = businesses.filter((b) => b.status === 'DRAFT');
  const activeBusinesses = businesses.filter((b) => b.status === 'ACTIVE');

  return (
    <UserDashboard>
      <Container>
        <Box mb={4}>
          <Typography variant="h4" fontWeight={700}>
            Business Workspace
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Manage your business profile, setup wizard, and objectives.
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress />
          </Box>
        ) : businesses.length === 0 && !showCreate ? (
          /* ── Empty state ── */
          <Card variant="outlined" sx={{ maxWidth: 480, mx: 'auto', mt: 6 }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h6" fontWeight={600} mb={1}>
                Set up your Business Workspace
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                Create a business profile, complete the setup wizard, and track
                your objectives — all in one place.
              </Typography>

              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Business Type</InputLabel>
                <Select
                  value={newType}
                  label="Business Type"
                  onChange={(e) => setNewType(e.target.value)}
                >
                  {TEMPLATE_TYPES.map((t) => (
                    <MenuItem key={t.value} value={t.value}>
                      {t.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                size="small"
                label="Business Name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                sx={{ mb: 3 }}
              />

              <Button
                variant="contained"
                fullWidth
                disabled={!newName.trim() || creating}
                onClick={handleCreate}
              >
                {creating ? 'Creating…' : 'Get Started'}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* ── DRAFT cards ── */}
            {draftBusinesses.length > 0 && (
              <Box mb={4}>
                <Typography variant="subtitle1" fontWeight={600} mb={2}>
                  In Progress
                </Typography>
                <Grid container spacing={2}>
                  {draftBusinesses.map((b) => (
                    <Grid item xs={12} sm={6} md={4} key={b.id}>
                      <Card
                        variant="outlined"
                        sx={{ borderColor: 'warning.main' }}
                      >
                        <CardContent>
                          <Typography variant="h6" fontWeight={600} mb={0.5}>
                            {b.businessName}
                          </Typography>
                          <Chip
                            label={b.status}
                            size="small"
                            color={statusColor(b.status)}
                            sx={{ mb: 1.5 }}
                          />
                          {b.templateType && (
                            <Chip
                              label={b.templateType.replace('_', ' ')}
                              size="small"
                              color={templateTypeColor(b.templateType)}
                              sx={{ ml: 1, mb: 1.5 }}
                            />
                          )}
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            mb={2}
                          >
                            Setup {b.setupStatus ?? 'not started'}
                          </Typography>
                          <Button
                            variant="contained"
                            size="small"
                            fullWidth
                            onClick={() =>
                              navigate(`/user/business/${b.id}/setup`)
                            }
                          >
                            Continue Setup
                          </Button>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* ── ACTIVE cards ── */}
            {activeBusinesses.length > 0 && (
              <Box mb={4}>
                <Typography variant="subtitle1" fontWeight={600} mb={2}>
                  Active Businesses
                </Typography>
                <Grid container spacing={2}>
                  {activeBusinesses.map((b) => (
                    <Grid item xs={12} sm={6} md={4} key={b.id}>
                      <Card
                        variant="outlined"
                        sx={{
                          borderColor: 'success.main',
                          cursor: 'pointer',
                        }}
                        onClick={() => navigate(`/user/business/${b.id}`)}
                      >
                        <CardContent>
                          <Typography variant="h6" fontWeight={600} mb={0.5}>
                            {b.businessName}
                          </Typography>
                          <Box display="flex" gap={1} flexWrap="wrap" mb={1}>
                            <Chip
                              label={b.status}
                              size="small"
                              color={statusColor(b.status)}
                            />
                            {b.templateType && (
                              <Chip
                                label={b.templateType.replace('_', ' ')}
                                size="small"
                                color={templateTypeColor(b.templateType)}
                              />
                            )}
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            {b.objectivesCount} objective
                            {b.objectivesCount !== 1 ? 's' : ''}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* ── New business inline form ── */}
            {showCreate ? (
              <Card variant="outlined" sx={{ maxWidth: 480 }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="subtitle1" fontWeight={600} mb={2}>
                    New Business
                  </Typography>
                  <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                    <InputLabel>Business Type</InputLabel>
                    <Select
                      value={newType}
                      label="Business Type"
                      onChange={(e) => setNewType(e.target.value)}
                    >
                      {TEMPLATE_TYPES.map((t) => (
                        <MenuItem key={t.value} value={t.value}>
                          {t.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    fullWidth
                    size="small"
                    label="Business Name"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    sx={{ mb: 2 }}
                  />
                  <Box display="flex" gap={1}>
                    <Button
                      variant="contained"
                      disabled={!newName.trim() || creating}
                      onClick={handleCreate}
                    >
                      {creating ? 'Creating…' : 'Get Started'}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setShowCreate(false);
                        setNewName('');
                      }}
                    >
                      Cancel
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            ) : (
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setShowCreate(true)}
              >
                + New Business
              </Button>
            )}
          </>
        )}
      </Container>
    </UserDashboard>
  );
};

export default BusinessHomePage;
